/**
 * Servicio específico para ConsultaRedV2
 * Centraliza todas las operaciones relacionadas con la consulta de red V2
 * Incluye llamadas a servicios, transformación de datos y manejo de respuestas
 */

import { httpClient } from './httpClient.service';
import { consultaRedV2Adapter } from './adapters/consultaRedV2.adapter';
import { nonComplianceAdapter } from './adapters/nonCompliance.adapter';

export class ConsultaRedV2Service {
  constructor() {
    this.serviceEndpoint = 'GetAppointmentsCheckerAPIV2';
    this.serviceV1Endpoint = 'GetAppointmentsCheckerAPIV1';
    this.proxy = 'ServicioTransar.svc';
  }

  /**
   * Consulta el servicio V2 de direccionamiento para obtener médico y sedes
   * @param {object} payload - Objeto con userTypeId, userId, serviceCode, etc
   * @returns {Promise} - Respuesta del servicio V2
   */
  async getAppointmentsCheckerV2(payload) {
    try {
      const response = await httpClient.post(payload, this.serviceEndpoint, this.proxy);
      return response;
    } catch (error) {
      console.error('❌ ConsultaRedV2Service - Error en getAppointmentsCheckerV2:', error);
      throw error;
    }
  }

  /**
   * Consulta el servicio V1 como fallback si V2 falla
   * @param {object} payload - Objeto con userTypeId, userId, serviceCode, etc
   * @returns {Promise} - Respuesta del servicio V1
   */
  async getAppointmentsCheckerV1(payload) {
    try {
      const response = await httpClient.post(payload, this.serviceV1Endpoint, this.proxy);
      return response;
    } catch (error) {
      console.error('❌ ConsultaRedV2Service - Error en getAppointmentsCheckerV1:', error);
      throw error;
    }
  }

  /**
   * Construye el payload para la solicitud del servicio V2
   * @param {object} options - { userTypeId, userId, serviceCode, isPromotionAndPrevention, agrupador }
   * @returns {object}
   */
  buildRequestPayload(options) {
    const { userTypeId, userId, serviceCode, isPromotionAndPrevention, agrupador } = options;

    return {
      userTypeId: userTypeId,
      userId: userId,
      serviceCode: serviceCode,
      isPromotionAndPrevention: serviceCode === '' || serviceCode === undefined ? 'true' : isPromotionAndPrevention,
      isPersonalDoctor: 'false',
      AplicativoAgenda: 0,
    };
  }

  /**
   * Procesa la respuesta exitosa (200) del servicio V2
   * @param {object} response - Respuesta del servicio
   * @param {object} options - { agrupador, specialtySelected, dataNameSelected }
   * @returns {object} - Datos procesados listos para el estado del componente
   */
  processSuccessResponse(response, options) {
    const { agrupador, specialtySelected, dataNameSelected } = options;

    if (!response || !response.Result) {
      throw new Error('Respuesta inválida del servicio V2');
    }

    const { Result } = response;
    const processedBranches = consultaRedV2Adapter.adaptBranches(
      Result.AvailableBranchesConsultaRed
    );

    const medicalAvailable = consultaRedV2Adapter.buildMedicalAvailable(
      response,
      processedBranches,
      specialtySelected,
      agrupador
    );

    const hasPersonalDoctor = consultaRedV2Adapter.hasPersonalDoctor(
      response,
      agrupador,
      specialtySelected
    );

    return {
      success: true,
      data: {
        medicalAvailable: medicalAvailable,
        branches: processedBranches,
        affiliatedContactInfo: Result.affiliatedContactInfo,
        hasAssignedAppointments: Result.UserHasAssignedAppointments || false,
        hasNonCompliances: Result.UserHasNonCompliances || false,
        nonCompliances: Result.NonCompliances || [],
        hasPersonalDoctor: hasPersonalDoctor,
        dataNameSelected: dataNameSelected,
      },
      hasPersonalDoctor: hasPersonalDoctor,
      oneBranche: processedBranches.length === 1,
      selectedBranch: processedBranches.length === 1 ? processedBranches[0] : null,
    };
  }

  /**
   * Maneja errores de la respuesta
   * @param {object} response - Respuesta del servicio
   * @returns {object} - Información del error
   */
  handleErrorResponse(response) {
    const isServerError = httpClient.isServerError(response);
    const isValidationError = httpClient.isValidationError(response);

    return {
      success: false,
      error: true,
      errorCode: response.ResponseCode,
      errorMessage: response.ResponseDetail || 'Error desconocido',
      isServerError: isServerError,
      isValidationError: isValidationError,
      shouldFallbackToV1: isServerError || isValidationError,
    };
  }

  /**
   * Procesa el estado de incumplimientos
   * @param {array} nonCompliances - Arreglo de incumplimientos
   * @param {function} getDaysBetweenDates - Función para calcular días
   * @returns {object} - Información de incumplimientos procesada
   */
  processNonCompliances(nonCompliances, getDaysBetweenDates) {
    if (!nonComplianceAdapter.hasNonCompliances(nonCompliances)) {
      return {
        hasNonCompliances: false,
        type: 'none',
        pedagogic: [],
        economic: [],
      };
    }

    const pedagogicNC = nonComplianceAdapter.getPedagogicNonCompliances(
      nonCompliances,
      getDaysBetweenDates
    );
    const economicNC = nonComplianceAdapter.getEconomicNonCompliances(
      nonCompliances,
      getDaysBetweenDates
    );

    const type = nonComplianceAdapter.determineNonComplianceType(pedagogicNC, economicNC);

    return {
      hasNonCompliances: pedagogicNC.length > 0 || economicNC.length > 0,
      type: type,
      pedagogic: pedagogicNC,
      economic: economicNC,
      pedagogicCups: nonComplianceAdapter.getCupsDescriptions(pedagogicNC),
      economicCups: nonComplianceAdapter.getCupsDescriptions(economicNC),
    };
  }

  /**
   * Flujo completo: Consulta V2 con fallback a V1
   * @param {object} options - Opciones de la consulta
   * @returns {Promise}
   */
  async consultWithFallback(options) {
    try {
      const payload = this.buildRequestPayload(options);
      const response = await this.getAppointmentsCheckerV2(payload);

      if (httpClient.isSuccess(response)) {
        return this.processSuccessResponse(response, {
          agrupador: options.agrupador,
          specialtySelected: options.specialtySelected,
          dataNameSelected: options.dataNameSelected,
        });
      } else {
        const errorInfo = this.handleErrorResponse(response);
        if (errorInfo.shouldFallbackToV1) {
          console.log('⚠️ ConsultaRedV2 Error - Intentando fallback a V1');
          return {
            ...errorInfo,
            fallbackToV1: true,
          };
        }
        return errorInfo;
      }
    } catch (error) {
      console.error('❌ ConsultaRedV2Service - Error en consultWithFallback:', error);
      return {
        success: false,
        error: true,
        errorMessage: error.message,
        fallbackToV1: true,
        isCatchError: true,
      };
    }
  }
}

export const consultaRedV2Service = new ConsultaRedV2Service();
